import React from 'react'
import { ArrowLeftIcon } from '@mote/assets/icons'
import { isNumber } from '@mote/helpers'
import styled, { css, th } from '@xstyled/styled-components'
import { Block } from '../block/block'
import { CloseButton } from '../closeButton/closeButton'
import { VisuallyHidden } from '../visuallyHidden/visuallyHidden'
import { TRANSITION_DURATION, usePanel } from './usePanel'

export const Panel = ({
  className,
  triggerElement,
  triggerElementContent,
  ...rest
}) => {
  const {
    menuProps,
    panelProps,
    triggerProps,
    children,
    level,
    variant,
    goUp,
    hidePanel
  } = usePanel(rest)

  const triggerBtn = React.useMemo(() => {
    const screenReaderText = triggerProps['aria-label'] ? (
      <VisuallyHidden>{triggerProps['aria-label']}</VisuallyHidden>
    ) : null

    return !triggerElement ? (
      <TriggerElementWrapper {...triggerProps}>
        {screenReaderText}
        {triggerElementContent}
      </TriggerElementWrapper>
    ) : (
      React.cloneElement(
        triggerElement,
        { ...triggerProps, ...triggerElement.props },
        <>
          {screenReaderText}
          {triggerElement.props.children}
        </>
      )
    )
  }, [triggerElement, triggerElementContent, triggerProps])

  return (
    <Root className={className}>
      {triggerBtn}
      <PanelRoot as='section' {...panelProps}>
        <Header isSubmenu={level > 0}>
          {level > 0 && (
            <BackButton variant={variant} size={36} onClick={goUp}>
              <ArrowLeftIcon />
            </BackButton>
          )}
          <StyledCloseButton variant={variant} size={36} onClick={hidePanel} />
        </Header>
        <Menu as='ul' layout='vertical' spreadX spreadY {...menuProps}>
          {children}
        </Menu>
      </PanelRoot>
    </Root>
  )
}

const Root = styled.div`
  display: inline-block;
  max-width: 100%;
  max-height: 100%;
`

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: ${({ isSubmenu }) =>
    isSubmenu ? 'space-between' : 'flex-end'};
  height: 48px;
  padding: 0 6px;
  flex-shrink: 0;

  ${({ variant }) => css`
    background: ${variant === 'light' ? 'white' : 'black'};
  `};
`

const Menu = styled(Block)`
  padding: 0;
  overflow-y: auto;
  scroll-snap-type: y proximity;
  outline: none;

  ${({ variant }) => css`
    li {
      color: ${variant === 'light' ? 'black' : 'white'};

      &:focus,
      &:hover {
        background: ${th.color(
          variant === 'light' ? 'grey.l4' : 'whiteAlpha.400'
        )};
      }
    }
  `};

  &:focus {
    outline: none;
  }
`

const PanelRoot = styled(Block)`
  flex-shrink: 0;
  z-index: 50;
  top: 0;
  padding: 0 0 6px 0;
  overflow: hidden;
  opacity: 0;
  outline: none;
  visibility: hidden;
  position: ${({ offCanvas }) => (offCanvas ? 'fixed' : 'absolute')};
  border-radius: ${({ offCanvas }) => (!offCanvas ? '8px' : null)};
  transform: ${({ offCanvas }) => (!offCanvas ? 'scale(0.9)' : null)};
  transition-property: opacity, visibility, transform;
  transition-duration: ${TRANSITION_DURATION}ms;
  transition-timing-function: ease-in-out;

  &:focus {
    outline: none;
  }

  ${({ variant }) => css`
    background: ${variant === 'light' ? 'white' : 'black'};
  `};

  ${({
    position,
    offCanvas,
    width,
    height,
    maxHeight,
    maxWidth,
    minHeight,
    minWidth
  }) => {
    if (!offCanvas) {
      return css`
        height: ${isNumber(height) ? `${height}px` : height};
        width: ${isNumber(width) ? `${width}px` : width};
        max-height: ${isNumber(maxHeight) ? `${maxHeight}px` : maxHeight};
        max-width: ${isNumber(maxWidth) ? `${maxWidth}px` : maxWidth};
        min-height: ${isNumber(minHeight) ? `${minHeight}px` : minHeight};
        min-width: ${isNumber(minWidth) ? `${minWidth}px` : minWidth};
      `
    }

    switch (position) {
      case 'top':
      case 'bottom':
        return css`
          height: ${isNumber(height) ? `${height}px` : height};
          width: 100%;
          min-width: 100vw;
        `
      case 'left':
      case 'right':
        return css`
          width: ${isNumber(width) ? `${width}px` : width};
          height: 100%;
          min-height: 100vh;
        `
    }
  }};

  ${({ position, offCanvas }) => {
    if (!offCanvas) {
      switch (position) {
        case 'bottom-left':
          return css`
            left: 0;
            bottom: 100%;
            transform-origin: bottom left;
          `
        case 'bottom-right':
          return css`
            right: 0;
            bottom: 100%;
            transform-origin: bottom right;
          `
        case 'bottom-center':
          return css`
            left: 50%;
            bottom: 100%;
            transform: scale(0.9) translateX(-50%);
            transform-origin: bottom center;
          `
        case 'center':
          return css`
            left: 50%;
            top: 50%;
            transform: scale(0.9) translate(-50%, -50%);
            transform-origin: center;
          `
        case 'top-center':
          return css`
            left: 50%;
            transform: scale(0.9) translateX(-50%);
            transform-origin: top center;
          `
        case 'top-left':
          return css`
            left: 0;
            transform-origin: top left;
          `
        case 'top-right':
          return css`
            right: 0;
            transform-origin: top right;
          `
        default:
          return ''
      }
    }

    switch (position) {
      case 'top':
        return css`
          transform: translateY(-100%);
        `
      case 'bottom':
        return css`
          bottom: 100%;
          transform: translateY(100%);
        `
      case 'left':
        return css`
          left: 0;
          transform: translateX(-100%);
        `
      case 'right':
        return css`
          right: 0;
          transform: translateX(100%);
        `
      default:
        return ''
    }
  }};

  ${({ isOpen }) =>
    isOpen &&
    css`
      opacity: 1;
      visibility: visible;
    `};

  ${({ isOpen, position, offCanvas }) => {
    if (!isOpen) return
    if (!offCanvas) {
      switch (position) {
        case 'top-left':
        case 'top-right':
        case 'bottom-left':
        case 'bottom-right':
          return css`
            transform: scale(1);
          `
        case 'top-center':
        case 'bottom-center':
          return css`
            transform: scale(1) translateX(-50%);
          `
        case 'center':
          return css`
            transform: scale(1) translate(-50%, -50%);
          `
        default:
          return ''
      }
    }

    switch (position) {
      case 'top':
      case 'bottom':
        return css`
          transform: translateY(0);
        `
      case 'left':
      case 'right':
        return css`
          transform: translateX(0);
        `
      default:
        return ''
    }
  }};
`

const TriggerElementWrapper = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: black;
  font-size: 15px;
  font-weight: 400;
  line-height: 120%;
  border-radius: 4px;
  height: 40px;
  width: auto;
  cursor: pointer;
  appearance: none;
  user-select: none;
  white-space: nowrap;
  vertical-align: middle;
  border: 1px solid transparent;
  background: transparent;
  outline: transparent solid 2px !important;
  outline-offset: 2px;
  transition-property: all;
  transition-duration: ${TRANSITION_DURATION}ms;

  &:focus {
    box-shadow: outline-primary;
  }

  &:hover,
  &:focus {
    color: black;
    background: ${th.color('grey.l4')};
  }

  &:active {
    background: ${th.color('grey.l3')};
  }

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.5;
      pointer-events: none;
      cursor: not-allowed;
    `};
`

const Item = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: black;
  font-size: 15px;
  font-style: normal;
  height: 40px;
  line-height: 40px;
  padding: 0 12px;
  cursor: pointer;
  scroll-snap-align: start;
  user-select: none;
  outline: transparent solid 2px !important;
  outline-offset: 2px;
  transition: all 150ms ease-in;

  &:focus,
  &:hover {
    background: ${th.color('grey.l4')};
  }
`

const StyledCloseButton = styled(CloseButton)`
  border-radius: 50%;

  &:focus {
    box-shadow: none;
  }

  ${({ variant }) => css`
    color: ${variant === 'light' ? 'black' : 'white'};

    &:focus,
    &:hover {
      color: ${variant === 'light' ? 'black' : 'white'};
      background: ${variant === 'light' ? '#f6f6f9' : '#3b3b42'};
    }
  `};
`

const BackButton = styled.button`
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 50%;
  border: 0;
  padding: 0;
  appearance: none;
  user-select: none;
  background-color: transparent;
  outline: transparent solid 2px !important;
  outline-offset: 2px;
  transition: all 150ms ease-in;
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent;

  svg {
    height: 24px;
    width: 24px;
  }

  ${({ size }) =>
    size !== undefined &&
    css`
      width: ${size}px;
      height: ${size}px;
    `};

  ${({ variant }) => css`
    color: ${variant === 'light' ? 'black' : 'white'};

    &:focus,
    &:hover {
      color: ${variant === 'light' ? 'black' : 'white'};
      background: ${th.color(
        variant === 'light' ? 'grey.l4' : 'whiteAlpha.400'
      )};
    }
  `};
`

Panel.Item = Item
